import { useState } from 'react'
import { toast } from 'sonner'
import { RuleBuilder } from '@/components/common/RuleBuilder'
import type { FieldsResponse, PredicateNode } from '@/components/common/RuleBuilder'
import { useConfirm } from '@/components/common/ConfirmDialog'
import type { VisibilityRule } from '@/features/admin/access/types'
import { useDeleteTeamRule, useUpdateTeamRule } from '@/features/admin/access/hooks'
import { formatError } from './formatError'

export function TeamRuleEditor({
  teamId, objectType, rule, metadata,
}: {
  teamId: number
  objectType: string
  rule: VisibilityRule
  metadata: FieldsResponse
}) {
  const confirm = useConfirm()
  const [name, setName] = useState(rule.name)
  const [predicate, setPredicate] = useState<PredicateNode>(rule.predicate)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const update = useUpdateTeamRule(teamId, objectType)
  const del = useDeleteTeamRule(teamId, objectType)

  const submit = () => {
    setError(null)
    setSaved(false)
    if (!name.trim()) { setError('Nazwa reguły wymagana.'); return }
    update.mutate(
      { ruleId: rule.id, payload: { name, predicate } },
      { onSuccess: () => setSaved(true), onError: e => setError(formatError(e)) },
    )
  }

  return (
    <section className="border rounded p-3 bg-white flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <input
          value={name}
          onChange={e => { setName(e.target.value); setSaved(false) }}
          className="border rounded px-2 py-1 text-sm font-medium flex-1 max-w-md"
        />
        <div className="flex items-center gap-2">
          {saved && <span className="text-emerald-700 text-xs">✓ Zapisano</span>}
          <button
            type="button"
            onClick={submit}
            disabled={update.isPending}
            className="text-xs bg-sky-600 text-white rounded px-2 py-1 disabled:opacity-50"
          >{update.isPending ? '…' : 'Zapisz'}</button>
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: `Usunąć regułę "${rule.name}"?`,
                description: 'Operacja jest nieodwracalna.',
                confirmLabel: 'Usuń',
                variant: 'destructive',
              })
              if (ok) del.mutate(rule.id, { onError: e => toast.error(formatError(e)) })
            }}
            className="text-xs text-red-600 hover:text-red-800"
          >Usuń</button>
        </div>
      </header>

      <RuleBuilder
        metadata={metadata}
        value={predicate}
        onChange={p => { setPredicate(p); setSaved(false) }}
      />

      {error && <p className="text-xs text-red-600">{error}</p>}
    </section>
  )
}
